import { useEffect, useState } from 'react';

import type { Brand } from '../api/types';
import { useSession } from '../auth/session';

import { applyAccent } from './accent';
import { applyFavicon, applyTitle } from './browserTab';
import { useTheme } from './useTheme';
import { DEFAULT_BRAND } from './useBrand';

export interface MyBrand {
  brand: Brand;
  /** An object URL for the brand's logo — null while none is set, or not yet fetched. */
  logoUrl: string | null;
}

/**
 * The signed-in caller's own applied brand (`GET /v1/branding/mine`), applied
 * the same way `useBrand` does for the pre-auth sign-in card — except resolved
 * by the caller's tenant/partner ownership, not by hostname. The authenticated
 * app already knows who signed in, so there is nothing to guess from the
 * browser's hostname the way the sign-in card has to; reusing hostname
 * resolution here would only work when a tenant's hostname happens to match
 * what the browser is actually pointed at (true in production, not in a local
 * Docker dev environment on one shared host).
 *
 * Without this, "Apply to workspace" only ever coloured the tab that clicked
 * it — a refresh, or any other screen, fell back to the stylesheet default,
 * because nothing re-applied a tenant's saved accent, default light/dark mode
 * or logo for an authenticated session.
 */
export function useMyBrand(): MyBrand {
  const { api, status } = useSession();
  const { applyBrand } = useTheme();
  const [brand, setBrand] = useState<Brand>(DEFAULT_BRAND);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    const live = { current: true };
    void (async () => {
      try {
        const resolved = await api.branding.mine();
        if (!live.current) return;
        setBrand(resolved);
        if (resolved.theme?.primary != null) applyAccent(resolved.theme.primary);
        if (resolved.theme?.mode != null) applyBrand(resolved.theme.mode);
      } catch {
        /* fall back to the built-in Docsight brand */
      }
    })();
    return () => {
      live.current = false;
    };
  }, [api, status, applyBrand]);

  // Reverts to the static default on sign-out too, for the same reason as the
  // favicon effect below: AppShell (this hook's owner) unmounts on sign-out,
  // and it is that cleanup — not a status check here — that runs then.
  useEffect(() => {
    applyTitle(brand.product_name);
    return () => {
      applyTitle(null);
    };
  }, [brand.product_name]);

  // A second, independent fetch: the logo is a separate (larger, optional)
  // request, and `brand.logo_uri` — not a `blob://` URI an <img> can load —
  // only tells us whether one exists at all.
  useEffect(() => {
    if (status !== 'authenticated' || brand.logo_uri == null) {
      setLogoUrl(null);
      return undefined;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await api.branding.downloadMine('logo', controller.signal);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setLogoUrl(objectUrl);
      } catch {
        if (!controller.signal.aborted) setLogoUrl(null);
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [api, status, brand.logo_uri]);

  // Applies the same logo to the actual browser tab, reverting to the static
  // default the moment there's no configured logo (including on sign-out,
  // when this whole hook's owner — AppShell — unmounts and this cleanup is
  // what runs instead of the effect body reacting to a status change it
  // won't get to see).
  useEffect(() => {
    applyFavicon(logoUrl);
    return () => {
      applyFavicon(null);
    };
  }, [logoUrl]);

  return { brand, logoUrl };
}
